import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Image } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  PencilFill,
  People,
  PersonVcard,
  PersonBadge,
  CashStack,
  Broadcast,
  Newspaper,
  Sun,
  Diagram3,
  ListOl,
  Compass,
  GeoAlt,
  Wrench,
  Brush,
  Flag,
  CalendarEvent,
  Signpost,
  BarChartLine,
  Building,
  GraphUpArrow,
  Share,
  Folder,
  CameraVideo,
  PlayFill,
  BoxArrowUpRight,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import SpendingIcon from "../navigation/SpendingIcon";

// Bump this string on every release that should show the modal again.
// The value is stored verbatim in localStorage, so a user who has seen 3.5.0
// will not see it again until this constant changes.
export const WHATS_NEW_VERSION = "3.6.1";

const STORAGE_KEY = "whatsNewVersion";

// Fired in-tab when onboarding writes its completion flags. The native
// "storage" event only reaches OTHER tabs, so same-tab listeners need this.
export const ONBOARDING_FLAGS_CHANGED_EVENT = "govglance:onboarding-flags-changed";

export const notifyOnboardingFlagsChanged = () => {
  try {
    window.dispatchEvent(new Event(ONBOARDING_FLAGS_CHANGED_EVENT));
  } catch (error) {
    // Nothing to do; the route-change re-check below is the fallback.
  }
};

// ─── Previews ────────────────────────────────────────────────────────────────
// Only the current feature release carries these. Older releases are read as
// history, and a screen someone has been using for a month does not need
// illustrating. A patch release keeps the previews on the feature release
// before it: someone updating straight from 3.5.1 to 3.6.1 has never seen the
// 3.6.0 screens, and walks back to them one link from the top.
//
// Each preview is the real component's markup and the real class names -
// .upcoming-elections, .ue-next-*, .ue-select-*, .ue-level-* from
// UpcomingElections.css, .reps-tabs-row, .reps-tab and .reps-precision-nudge
// from Representatives.css, .reps-mode-row from RepresentativesModes.css,
// .feed-item and .candidate-financials-* from FeedItem.css and
// CandidateFinancials.css, .feed-card and .feed-bar-* from FeedCard.css,
// .trade-* and .feed-item-details from Spending.css, .ballot-party* and
// .party-badge-dot from Parties.css, .record-lines and .member-tab* from
// MemberRecord.css and MemberDetailPage.css, .feed-item--stream,
// .feed-item--live, .stream-status-line and .stream-dot from Streams.css. Not
// lookalikes rebuilt with private styles: the same CSS that paints the actual
// screens paints these, so a change to either screen lands here too and the
// preview cannot quietly go out of date.
//
// The flip side, and the reason a preview can go blank: a class name that no
// longer exists in the feature's stylesheet inherits nothing at all. When a
// screen is reworked, its preview has to be re-read off the new markup rather
// than assumed to still match.
//
// Those stylesheets are imported by their own components and CRA bundles CSS
// globally, so they are present whether or not this modal imports them.
//
// Inert: tabIndex -1 keeps the fake controls out of the tab order, and the
// wrapper is aria-hidden where it is rendered.

// The one-line answer at the top of the panel: label, date, countdown. Shown
// in its at-home wording, which is the branch that says "Your next election"
// rather than naming a state.
const NextElectionPreview = () => (
  <div className="wn-preview">
    <div className="upcoming-elections">
      <div className="ue-next">
        <span className="ue-next-label">Your next election</span>
        <span className="ue-next-date">Tuesday, November 3, 2026</span>
        <span className="ue-next-countdown">In 46 days</span>
      </div>
    </div>
  </div>
);

// The controls row while browsing away from home: state pill, county pill, and
// the reset back to the saved state. The reset is the whole point of the
// miniature -- it is what says the dropdown browses rather than moves house.
const StatePickerPreview = () => (
  <div className="wn-preview">
    <div className="upcoming-elections">
      <div className="ue-controls">
        <div className="ue-select">
          <button type="button" className="ue-select-toggle" tabIndex={-1}>
            <GeoAlt size={12} className="me-1" />
            <span>Ohio</span>
            <ChevronDown size={12} className="ms-1" />
          </button>
        </div>

        <div className="ue-select">
          <button type="button" className="ue-select-toggle" tabIndex={-1}>
            <GeoAlt size={12} className="me-1" />
            <span>Franklin County</span>
            <ChevronDown size={12} className="ms-1" />
          </button>
        </div>

        <button type="button" className="ue-reset" tabIndex={-1}>
          Back to Indiana
        </button>
      </div>
    </div>
  </div>
);

// The district dropdown beside the state, on Federal: the reader's own
// congressional district picked, which is where it starts at home. The level
// tabs sit under it so the miniature says which level the district belongs to.
const DistrictPickerPreview = () => (
  <div className="wn-preview">
    <div className="upcoming-elections">
      <div className="ue-controls">
        <div className="ue-select">
          <button type="button" className="ue-select-toggle" tabIndex={-1}>
            <GeoAlt size={12} className="me-1" />
            <span>Indiana</span>
            <ChevronDown size={12} className="ms-1" />
          </button>
        </div>

        <div className="ue-select">
          <button type="button" className="ue-select-toggle" tabIndex={-1}>
            <GeoAlt size={12} className="me-1" />
            <span>District 5</span>
            <ChevronDown size={12} className="ms-1" />
          </button>
        </div>
      </div>

      <div className="ue-levels">
        <button type="button" className="ue-level ue-level-active" tabIndex={-1}>
          Federal
        </button>
        <button type="button" className="ue-level" tabIndex={-1}>
          State
        </button>
        <button type="button" className="ue-level" tabIndex={-1}>
          County
        </button>
        <button type="button" className="ue-level" tabIndex={-1}>
          City
        </button>
      </div>
    </div>
  </div>
);

// The level tabs over the candidate list, County selected — the default, since
// the reader's own county is pre-applied from their saved location.
const LevelsPreview = () => (
  <div className="wn-preview">
    <div className="upcoming-elections">
      <div className="ue-levels">
        <button type="button" className="ue-level" tabIndex={-1}>
          State
        </button>
        <button type="button" className="ue-level ue-level-active" tabIndex={-1}>
          County
        </button>
        <button type="button" className="ue-level" tabIndex={-1}>
          City
        </button>
      </div>
    </div>
  </div>
);

// The parties strip from the Elections panel and the party page: each label on
// the ballot with its candidate count, and the bar of the ballot's make-up
// under them. Independent is a dashed chip because it names no party and
// opens no page -- the strip shows it rather than implying every candidate has
// a party.
const BALLOT_PARTY_ROWS = [
  { label: "Republican", count: 14, color: "#d4383b" },
  { label: "Democratic", count: 13, color: "#2f6bd6" },
  { label: "Libertarian", count: 4, color: "#e0b100" },
];

const PartiesPreview = () => (
  <div className="wn-preview">
    <div className="ballot-parties">
      <div className="ballot-parties-head">
        <span className="ballot-parties-title">Parties on the ballot</span>
        <span className="ballot-parties-hint">Tap one for its platform, news and money</span>
      </div>
      <div className="ballot-parties-row">
        {BALLOT_PARTY_ROWS.map((row) => (
          <button
            key={row.label}
            type="button"
            className="ballot-party"
            style={{ "--party-color": row.color }}
            tabIndex={-1}
          >
            <span className="party-badge-dot" style={{ backgroundColor: row.color }} />
            <span className="ballot-party-label">{row.label}</span>
            <span className="ballot-party-count">{row.count}</span>
          </button>
        ))}
        <span className="ballot-party ballot-party--plain">
          <span className="party-badge-dot" style={{ backgroundColor: "#8e8e93" }} />
          <span className="ballot-party-label">Independent</span>
          <span className="ballot-party-count">3</span>
        </span>
      </div>
      <div className="ballot-parties-bar">
        {BALLOT_PARTY_ROWS.map((row) => (
          <span key={row.label} style={{ flexGrow: row.count, backgroundColor: row.color }} />
        ))}
        <span style={{ flexGrow: 3, backgroundColor: "#8e8e93" }} />
      </div>
    </div>
  </div>
);

// The budget panel's bars: what the government is spending on, by purpose,
// each as a share of every agency's obligations.
const BUDGET_ROWS = [
  { name: "Social Security", value: "$1.52tn", share: 0.16 },
  { name: "Medicare", value: "$1.04tn", share: 0.11 },
  { name: "Net interest", value: "$0.99tn", share: 0.1 },
];

const SpendingPreview = () => (
  <div className="wn-preview">
    <div className="feed-item feed-item--fullwidth feed-card">
      <Card>
        <Card.Body className="feed-card-body">
          <ul className="feed-bars feed-bars--gov">
            {BUDGET_ROWS.map((row) => (
              <li key={row.name} className="feed-bar">
                <div className="feed-bar-row">
                  <span className="feed-bar-name">{row.name}</span>
                  <span className="feed-bar-value">{row.value}</span>
                </div>
                <div className="feed-bar-track">
                  <span className="feed-bar-fill" style={{ width: `${row.share * 100}%` }} />
                </div>
                <div className="feed-bar-sub">{Math.round(row.share * 100)}%</div>
              </li>
            ))}
          </ul>
        </Card.Body>
      </Card>
    </div>
  </div>
);

// A live stream as it appears at the top of For You: a feed item with the
// government as its source, the accent ring, and the pulsing status line. The
// same markup StreamCard gets from FeedItem, minus Save, Share and Hide,
// which are the feed's own buttons and need no introduction. A made-up city,
// so the notes never show a real meeting as live.
const LiveStreamPreview = () => (
  <div className="wn-preview">
    <div className="feed-item feed-item--default feed-item--recents feed-item--details feed-item--stream feed-item--live">
      <Card>
        <Card.Title id="billBodyTextSource">
          Riverton city
          <ChevronRight id="clickChevron" className="heading-chevron" />
        </Card.Title>
        <Card.Title id="billText" className="card-title p-3">
          City Council Meeting: Regular Session
        </Card.Title>
        <Card.Body className="feed-item-details">
          <div className="feed-item-detail">
            <span className="stream-status-line stream-status-line--live">
              <span className="stream-dot" />
              City · Live now · live for 42 min · 118 watching
            </span>
          </div>
        </Card.Body>
        <Card.Footer className="d-flex">
          <Button className="clickable me-2" id="billLink" tabIndex={-1}>
            <PlayFill size={16} /> Watch live
          </Button>
          <Button className="clickable me-2" id="viewBillButton" tabIndex={-1}>
            <BoxArrowUpRight size={12} /> YouTube
          </Button>
        </Card.Footer>
      </Card>
    </div>
  </div>
);

// One reported trade as the feed shows it: whose it was, what kind, the
// ticker, the range exactly as reported and how long after it was filed. A
// made-up member, so the notes never put a real person's trade in front of
// every reader.
const TradePreview = () => (
  <div className="wn-preview">
    <div className="feed-item feed-item--default feed-item--details">
      <Card>
        <Card.Title id="billText" className="card-title p-3">
          Jordan Ellis, OH07
        </Card.Title>
        <Card.Body className="feed-item-details">
          <div className="feed-item-detail">
            <span className="trade-what">
              <span className="trade-owner">Spouse</span>
              <span className="trade-type">Purchase</span>
              <span className="trade-ticker">ACME</span>
            </span>
          </div>
          <div className="feed-item-detail">
            <span className="trade-asset">Acme Corporation Common Stock</span>
          </div>
          <div className="feed-item-detail">
            <span className="trade-facts">
              <span className="trade-amount">$15,001 - $50,000</span>
              <span>traded Aug 4</span>
              <span>reported 21 days after</span>
            </span>
          </div>
        </Card.Body>
      </Card>
    </div>
  </div>
);

// A member's page: the record's counts written as lines under the profile,
// and the tab strip they open.
const MemberRecordPreview = () => (
  <div className="wn-preview">
    <div className="record-lines">
      <button type="button" className="record-line-link" tabIndex={-1}>
        38 bills sponsored, 2 became law
      </button>
      <button type="button" className="record-line-link" tabIndex={-1}>
        212 bills cosponsored
      </button>
      <button type="button" className="record-line-link" tabIndex={-1}>
        673 roll calls, missed 1.5%
      </button>
      <button type="button" className="record-line-link" tabIndex={-1}>
        Campaign raised $1.8m, $2.6m on hand
      </button>
    </div>
    <div className="member-tabs member-tabs--scroll wn-preview-row">
      <button type="button" className="member-tab member-tab--active" tabIndex={-1}>
        Votes
      </button>
      <button type="button" className="member-tab" tabIndex={-1}>
        Sponsored
      </button>
      <button type="button" className="member-tab" tabIndex={-1}>
        Cosponsored
      </button>
      <button type="button" className="member-tab" tabIndex={-1}>
        Campaign $
      </button>
      <button type="button" className="member-tab" tabIndex={-1}>
        Stock trades
      </button>
    </div>
  </div>
);

// The Officials / Candidates toggle, shown mid-switch: Candidates selected, so
// the miniature says what the feature is rather than showing the page's
// resting state.
const RunningPreview = () => (
  <div className="wn-preview">
    <div className="reps-mode-row">
      <button type="button" className="reps-mode" tabIndex={-1}>
        Officials
      </button>
      <button type="button" className="reps-mode reps-mode-active" tabIndex={-1}>
        Candidates
      </button>
    </div>

    <div className="reps-tabs-row wn-preview-row">
      <button type="button" className="reps-location-button" tabIndex={-1}>
        <span>Carmel, IN</span>
        <PencilFill size={11} className="reps-location-pencil" />
      </button>

      <div className="reps-tabs">
        <button type="button" className="reps-tab" tabIndex={-1}>
          State
        </button>
        <button type="button" className="reps-tab reps-tab-active" tabIndex={-1}>
          County
        </button>
        <button type="button" className="reps-tab" tabIndex={-1}>
          City
        </button>
      </div>
    </div>
  </div>
);

// One local candidate card, with the filing link that is the whole point of
// these rows: below federal level there is no campaign finance, and the
// government page the name was read from is what a reader can check instead.
const LocalCandidatePreview = () => (
  <div className="wn-preview">
    <div className="feed-item feed-item--default">
      <Card>
        <Card.Title id="billText" className="card-title p-3">
          Wendy Dant Chesser
        </Card.Title>
        <Card.Body id="billBodyText">County Commissioner (Candidate)</Card.Body>
        <Card.Body id="billBodyText">Republican · Hamilton County</Card.Body>
        <Card.Body id="billBodyText">Election: 2026</Card.Body>
      </Card>
    </div>
  </div>
);

const FinancePreview = () => (
  <div className="wn-preview">
    <div className="candidate-financials">
      <p className="candidate-financials-coverage">
        2026 cycle · Reported through 6-30-2026
      </p>

      <div className="feed-item feed-item--default">
        <Card>
          <Card.Title id="billText" className="card-title p-3">
            Total Raised
          </Card.Title>

          <Card.Body id="billBodyText">
            <dl className="candidate-financials-list">
              <div className="candidate-financials-row">
                <dt className="candidate-financials-label">Total receipts</dt>
                <dd className="candidate-financials-value">$1,284,306.12</dd>
              </div>
              <div className="candidate-financials-row">
                <dt className="candidate-financials-label">
                  Itemized individual contributions
                </dt>
                <dd className="candidate-financials-value">$612,940.00</dd>
              </div>
              <div className="candidate-financials-row">
                <dt className="candidate-financials-label">
                  Other committee contributions
                </dt>
                <dd className="candidate-financials-value">$418,500.00</dd>
              </div>
            </dl>
          </Card.Body>
        </Card>
      </div>
    </div>
  </div>
);

// Release notes are English data, translated at render via t() with the English
// string as the fallback — same convention as NotificationsOnboarding.
//
// One entry per release, newest first. The modal opens on RELEASES[0] and a
// back link walks older versions, so somebody who skipped a release can still
// read what they missed instead of the notes being silently replaced.
//
// Adding a release: prepend it here and bump WHATS_NEW_VERSION to match its
// version string. A feature release takes the previews onto its own entry; a
// patch release leaves them where they are (see the note above the previews).
const RELEASES = [
  {
    version: "3.6.1",
    groups: [
      {
        id: "fixed",
        labelKey: "whatsNewGroupFixed",
        labelFallback: "Fixed",
        items: [
          {
            id: "elections-districts",
            icon: <ListOl size={18} />,
            titleKey: "whatsNewElectionsDistrictsTitle",
            titleFallback: "Elections by district",
            bodyKey: "whatsNewElectionsDistrictsBody",
            bodyFallback:
              "You can now narrow Elections to a single district. Pick one in the dropdown next to the state to see just that district's races, or choose All for the whole state. At home it starts on the districts you saved for Representatives.",
            preview: <DistrictPickerPreview />,
          },
          {
            id: "live-streams-ios",
            icon: <CameraVideo size={18} />,
            titleKey: "whatsNewLiveStreamsIosTitle",
            titleFallback: "Live streams on iPhone and iPad",
            bodyKey: "whatsNewLiveStreamsIosBody",
            bodyFallback:
              "Fixed a bug that stopped live streams and recordings from playing on iPhone and iPad.",
          },
        ],
      },
    ],
  },
  {
    version: "3.6.0",
    groups: [
      {
        id: "new",
        labelKey: "whatsNewGroupNew",
        labelFallback: "New",
        items: [
          {
            id: "elections-tab",
            icon: <Flag size={18} />,
            titleKey: "whatsNewElectionsTabTitle",
            titleFallback: "Elections",
            bodyKey: "whatsNewElectionsTabBody",
            bodyFallback:
              "A new Elections tab in the bottom bar, alongside Representatives, gathers election coverage in one place.",
          },
          {
            id: "spending-tab",
            icon: <SpendingIcon size={18} />,
            titleKey: "whatsNewSpendingTabTitle",
            titleFallback: "Spending",
            bodyKey: "whatsNewSpendingTabBody",
            bodyFallback:
              "Spending has its own tab in the bottom bar. Your saved Collection has moved to the menu and to Preferences.",
          },
          {
            id: "live-streams",
            icon: <CameraVideo size={18} />,
            titleKey: "whatsNewLiveStreamsTitle",
            titleFallback: "Live streams",
            bodyKey: "whatsNewLiveStreamsBody",
            bodyFallback:
              "Watch your city, county, state and federal government live, without leaving the app. When a meeting or hearing is on, it appears at the top of For You. Live streams in the Topic menu lists everything live, scheduled and recently recorded, and any stream can be saved to your collection.",
            preview: <LiveStreamPreview />,
          },
          {
            id: "next-election",
            icon: <CalendarEvent size={18} />,
            titleKey: "whatsNewNextElectionTitle",
            titleFallback: "Your next election",
            bodyKey: "whatsNewNextElectionBody",
            bodyFallback:
              "The next election date where you live, with how long there is to go, at the top of the Elections tab.",
            preview: <NextElectionPreview />,
          },
          {
            id: "parties",
            icon: <Diagram3 size={18} />,
            titleKey: "whatsNewPartiesTitle",
            titleFallback: "Parties on the ballot",
            bodyKey: "whatsNewPartiesBody",
            bodyFallback:
              "Every party on your ballot, with how many candidates it is running. Tap one for its own page: where it stands in its platform's own words, side by side with the others, its news, its money, and who it is running in your state.",
            preview: <PartiesPreview />,
          },
          {
            id: "who-is-running",
            icon: <PersonVcard size={18} />,
            titleKey: "whatsNewWhoIsRunningTitle",
            titleFallback: "Who is running",
            bodyKey: "whatsNewWhoIsRunningBody",
            bodyFallback:
              "State, county, and city candidates sit under the date, already filtered to your own county. Switch level with the tabs or pick a different county.",
            preview: <LevelsPreview />,
          },
          {
            id: "elections-elsewhere",
            icon: <GeoAlt size={18} />,
            titleKey: "whatsNewElectionsElsewhereTitle",
            titleFallback: "Look up another state",
            bodyKey: "whatsNewElectionsElsewhereBody",
            bodyFallback:
              "Browse any state's next election and candidates without changing the location you have saved. One tap goes back to your own.",
            preview: <StatePickerPreview />,
          },
          {
            id: "federal-spending",
            icon: <BarChartLine size={18} />,
            titleKey: "whatsNewFederalSpendingTitle",
            titleFallback: "Federal spending",
            bodyKey: "whatsNewFederalSpendingBody",
            bodyFallback:
              "The Spending page opens with what the government is spending on, by purpose, kind and agency, then where award money goes and the newest and largest awards, each with the agency and what it was for.",
            preview: <SpendingPreview />,
          },
          {
            id: "companies",
            icon: <Building size={18} />,
            titleKey: "whatsNewCompaniesTitle",
            titleFallback: "The companies behind the money",
            bodyKey: "whatsNewCompaniesBody",
            bodyFallback:
              "Rank companies by federal contracts, dependence on them, lobbying and PAC giving, or search for one. Each has its own page with its contracts, what it spends to be heard, and members' trades in its shares.",
          },
          {
            id: "stock-trades",
            icon: <GraphUpArrow size={18} />,
            titleKey: "whatsNewStockTradesTitle",
            titleFallback: "Members' stock trades",
            bodyKey: "whatsNewStockTradesBody",
            bodyFallback:
              "The largest stock trades members of Congress reported, with whose trade it was, the range exactly as reported, and how long after the trade it was filed.",
            preview: <TradePreview />,
          },
          {
            id: "member-record",
            icon: <PersonBadge size={18} />,
            titleKey: "whatsNewMemberRecordTitle",
            titleFallback: "A member's full record",
            bodyKey: "whatsNewMemberRecordBody",
            bodyFallback:
              "Every member of Congress now has their votes, sponsored and cosponsored bills, campaign money, stock trades, committees, companies tied to them, and financial disclosures on one page. Senators' votes are here for the first time.",
            preview: <MemberRecordPreview />,
          },
          {
            id: "us-news",
            icon: <Newspaper size={18} />,
            titleKey: "whatsNewUsNewsTitle",
            titleFallback: "City news",
            bodyKey: "whatsNewUsNewsBody",
            bodyFallback:
              "Your city now has its own feed in the Local tab.",
          },
          {
            id: "elections-coverage",
            icon: <Signpost size={18} />,
            titleKey: "whatsNewElectionsCoverageTitle",
            titleFallback: "What is and is not covered",
            bodyKey: "whatsNewElectionsCoverageBody",
            bodyFallback:
              "Where election dates are not collected nationally, the panel says so and links to that state's own election office instead of leaving a blank.",
          },
        ],
      },
      {
        id: "improved",
        labelKey: "whatsNewGroupImproved",
        labelFallback: "Improved",
        items: [
          {
            id: "bill-vote-pages",
            icon: <Share size={18} />,
            titleKey: "whatsNewBillVotePagesTitle",
            titleFallback: "Bills and votes",
            bodyKey: "whatsNewBillVotePagesBody",
            bodyFallback:
              "More on a member's bills and votes opens the full bill or roll call, and shared links now open the same page for whoever you send them to.",
          },
          {
            id: "collection-moved",
            icon: <Folder size={18} />,
            titleKey: "whatsNewCollectionMovedTitle",
            titleFallback: "Your collection",
            bodyKey: "whatsNewCollectionMovedBody",
            bodyFallback:
              "Saved items are one tap away in Preferences and the menu.",
          },
        ],
      },
      {
        id: "fixed",
        labelKey: "whatsNewGroupFixed",
        labelFallback: "Fixed",
        items: [
          {
            id: "bug-fixes",
            icon: <Wrench size={18} />,
            titleKey: "whatsNewBugFixesTitle",
            titleFallback: "General bug fixes",
            bodyKey: "whatsNewBugFixesBody",
            bodyFallback: "A round of fixes across the app.",
          },
          {
            id: "ui-fixes",
            icon: <Brush size={18} />,
            titleKey: "whatsNewUiFixesTitle",
            titleFallback: "General UI fixes",
            bodyKey: "whatsNewUiFixesBody",
            bodyFallback:
              "Spacing, buttons, and dropdowns tidied up throughout, on phones and on desktop.",
          },
        ],
      },
    ],
  },
  {
    version: "3.5.1",
    groups: [
      {
        id: "new",
        labelKey: "whatsNewGroupNew",
        labelFallback: "New",
        items: [
          {
            id: "candidates",
            icon: <PersonVcard size={18} />,
            titleKey: "whatsNewCandidatesTitle",
            titleFallback: "Candidates",
            bodyKey: "whatsNewCandidatesBody",
            bodyFallback:
              "Browse who is running for the U.S. House, Senate, and presidency. Filter by state, or open any candidate's own page.",
          },
          {
            id: "campaign-finance",
            icon: <CashStack size={18} />,
            titleKey: "whatsNewCampaignFinanceTitle",
            titleFallback: "Campaign finance",
            bodyKey: "whatsNewCampaignFinanceBody",
            bodyFallback:
              "Every candidate page shows what they raised, what they spent, and what is left, straight from their FEC filings.",
            preview: <FinancePreview />,
          },
          {
            id: "local-candidates",
            icon: <PersonVcard size={18} />,
            titleKey: "whatsNewLocalCandidatesTitle",
            titleFallback: "State, county, and city candidates",
            bodyKey: "whatsNewLocalCandidatesBody",
            bodyFallback:
              "Candidates now go past Congress, down to county commissions, school boards, and town councils. Open any of them for the contact details and the government filing their name came from.",
            preview: <LocalCandidatePreview />,
          },
          {
            id: "running-here",
            icon: <People size={18} />,
            titleKey: "whatsNewRunningHereTitle",
            titleFallback: "Who is running where you live",
            bodyKey: "whatsNewRunningHereBody",
            bodyFallback:
              "Your representatives page now switches between who holds office and who is running for it, filtered to your own county.",
            preview: <RunningPreview />,
          },
          {
            id: "county-city-split",
            icon: <Diagram3 size={18} />,
            titleKey: "whatsNewCountyCitySplitTitle",
            titleFallback: "County and city, told apart",
            bodyKey: "whatsNewCountyCitySplitBody",
            bodyFallback:
              "Local officials are grouped by the government they serve, under its own name, instead of one shared heading.",
          },
        ],
      },
      {
        id: "fixed",
        labelKey: "whatsNewGroupFixed",
        labelFallback: "Fixed",
        items: [
          {
            id: "address-districts",
            icon: <GeoAlt size={18} />,
            titleKey: "whatsNewAddressDistrictsTitle",
            titleFallback: "Districts by address",
            bodyKey: "whatsNewAddressDistrictsBody",
            bodyFallback: "Fixed an issue with looking up your districts by address.",
          },
          {
            id: "district-labels",
            icon: <ListOl size={18} />,
            titleKey: "whatsNewDistrictLabelsTitle",
            titleFallback: "District names",
            bodyKey: "whatsNewDistrictLabelsBody",
            bodyFallback:
              "Districts are named the way your state names them, with the number attached, rather than repeating the same generic label.",
          },
          {
            id: "bug-fixes",
            icon: <Wrench size={18} />,
            titleKey: "whatsNewBugFixesTitle",
            titleFallback: "General bug fixes",
            bodyKey: "whatsNewBugFixesBody",
            bodyFallback: "A round of fixes across the app.",
          },
          {
            id: "ui-fixes",
            icon: <Brush size={18} />,
            titleKey: "whatsNewUiFixesTitle",
            titleFallback: "General UI fixes",
            bodyKey: "whatsNewUiFixesBody",
            bodyFallback:
              "Spacing, buttons, and dropdowns tidied up throughout, on phones and on desktop.",
          },
        ],
      },
    ],
  },
  {
    version: "3.5.0",
    groups: [
      {
        id: "new",
        labelKey: "whatsNewGroupNew",
        labelFallback: "New",
        items: [
          {
            id: "representatives",
            icon: <People size={18} />,
            titleKey: "whatsNewRepresentativesTitle",
            titleFallback: "Representatives",
            bodyKey: "whatsNewRepresentativesBody",
            bodyFallback:
              "Look up your federal, state, and local representatives in one place. Save a location once and your reps stay a tap away.",
          },
          {
            id: "updates",
            icon: <Broadcast size={18} />,
            titleKey: "whatsNewUpdatesTitle",
            titleFallback: "Federal, state, and local updates",
            bodyKey: "whatsNewUpdatesBody",
            bodyFallback:
              "A new updates feed tracks changes to official government websites, on the home page and on every state page.",
          },
          {
            id: "light-theme",
            icon: <Sun size={18} />,
            titleKey: "whatsNewLightThemeTitle",
            titleFallback: "New Light theme",
            bodyKey: "whatsNewLightThemeBody",
            bodyFallback:
              "A softer Light theme joins Classic, Dark, and Auto. Change it anytime in Settings.",
          },
        ],
      },
      {
        id: "improved",
        labelKey: "whatsNewGroupImproved",
        labelFallback: "Improved",
        items: [
          {
            id: "data",
            icon: <Diagram3 size={18} />,
            titleKey: "whatsNewDataTitle",
            titleFallback: "Fresher data",
            bodyKey: "whatsNewDataBody",
            bodyFallback:
              "Rebuilt data pipelines and updated API calls across feeds, members, and representatives.",
          },
          {
            id: "navigation",
            icon: <Compass size={18} />,
            titleKey: "whatsNewNavigationTitle",
            titleFallback: "Getting around",
            bodyKey: "whatsNewNavigationBody",
            bodyFallback:
              "Reworked desktop navigation, feed filtering, and the location picker, with tidier feed cards throughout.",
          },
        ],
      },
      {
        id: "fixed",
        labelKey: "whatsNewGroupFixed",
        labelFallback: "Fixed",
        items: [
          {
            id: "fixes",
            icon: <Wrench size={18} />,
            titleKey: "whatsNewFixesTitle",
            titleFallback: "Bug and interface fixes",
            bodyKey: "whatsNewFixesBody",
            bodyFallback:
              "Mobile dropdowns, modal buttons, spacing outside the US, and back button behavior all behave the way you expect again.",
          },
        ],
      },
    ],
  },
];

const readFlag = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeFlag = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // Storage can be unavailable in private browsing; failing to record the
    // version is not worth breaking the app over.
  }
};

const isOnboardingComplete = () =>
  readFlag("onboarding") === "true" &&
  readFlag("themeOnboarding") === "true" &&
  readFlag("languageOnboarding") === "true";

export const hasSeenWhatsNew = () => readFlag(STORAGE_KEY) === WHATS_NEW_VERSION;

export const markWhatsNewSeen = () => writeFlag(STORAGE_KEY, WHATS_NEW_VERSION);

const WhatsNewModal = ({ forceOpen = false, onClose }) => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  // Index into RELEASES. 0 is the current release; higher is further back.
  const [releaseIndex, setReleaseIndex] = useState(0);

  const release = RELEASES[releaseIndex];
  const hasOlder = releaseIndex < RELEASES.length - 1;
  const hasNewer = releaseIndex > 0;

  const evaluate = useCallback(() => {
    if (hasSeenWhatsNew()) {
      setShow(false);
      return;
    }
    // Show as soon as onboarding is settled — including the run that just
    // finished. Until then stay hidden rather than stacking on the onboarding
    // modal. Nothing is stamped here; only handleClose records the version, so
    // a user who never sees the modal stays eligible on the next load.
    setShow(isOnboardingComplete());
  }, []);

  useEffect(() => {
    if (forceOpen) {
      setShow(true);
      return;
    }

    evaluate();

    // Three ways the answer can change after this component mounts:
    //   1. Another tab finishes onboarding -> native "storage" event.
    //   2. This tab finishes onboarding    -> our custom event.
    //   3. Onboarding redirects to a home page -> pathname in the deps below.
    // (3) is the safety net: "storage" never fires in the tab that wrote the
    // value, so without it the modal waits for a full reload that a Capacitor
    // build rarely gets.
    window.addEventListener("storage", evaluate);
    window.addEventListener(ONBOARDING_FLAGS_CHANGED_EVENT, evaluate);
    return () => {
      window.removeEventListener("storage", evaluate);
      window.removeEventListener(ONBOARDING_FLAGS_CHANGED_EVENT, evaluate);
    };
  }, [evaluate, forceOpen, pathname]);

  // Always open on the current release, even if a previous look ended on an
  // older one.
  useEffect(() => {
    if (show) setReleaseIndex(0);
  }, [show]);

  const handleClose = useCallback(() => {
    // A forceOpen preview (the ?whatsnew=1 dev affordance) must not burn the
    // real flag — otherwise opening it once suppresses the modal for that
    // browser and you can no longer test the genuine gate.
    if (!forceOpen) markWhatsNewSeen();
    setShow(false);
    if (onClose) onClose();
  }, [onClose, forceOpen]);

  const goOlder = useCallback(() => {
    setReleaseIndex((value) => Math.min(RELEASES.length - 1, value + 1));
  }, []);

  const goNewer = useCallback(() => {
    setReleaseIndex((value) => Math.max(0, value - 1));
  }, []);

  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
      if (event.key === "ArrowLeft") goOlder();
      if (event.key === "ArrowRight") goNewer();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, handleClose, goOlder, goNewer]);

  if (!show || !release) return null;

  return (
    <>
      <style>{`
        .wn-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1100; padding: 16px; }
        .wn-modal { background-color: var(--onboarding-modal-bg, var(--bg-primary)); border-radius: 16px; width: 100%; max-width: 560px; max-height: 90vh; overflow: hidden; color: var(--text-primary); display: flex; flex-direction: column; box-shadow: 0 12px 40px var(--shadow-heavy); }
        .wn-header { padding: max(env(safe-area-inset-top, 16px), 24px) 20px 12px; display: flex; align-items: flex-start; gap: 12px; flex-shrink: 0; }
        .wn-header-logo { flex-shrink: 0; }
        .wn-header-text { flex: 1; min-width: 0; }
        .wn-eyebrow { display: inline-block; font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--accent-primary); margin-bottom: 4px; }
        .wn-title { margin: 0; font-size: 1.3rem; font-weight: 600; color: var(--text-primary); }
        .wn-subtitle { margin: 4px 0 0; font-size: 14px; color: var(--text-muted); }
        .wn-close-btn { background: none; border: none; color: var(--text-tertiary); cursor: pointer; padding: 4px; display: flex; align-items: center; border-radius: 50%; flex-shrink: 0; }
        .wn-close-btn:hover { color: var(--text-primary); background-color: var(--bg-card-hover); }
        .wn-close-btn:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }
        .wn-content { padding: 4px 20px 8px; flex: 1; overflow-y: auto; }
        .wn-group { margin-bottom: 20px; }
        .wn-group:last-child { margin-bottom: 4px; }
        .wn-group-label { margin: 0 0 10px; font-size: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); }
        .wn-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }

        /* A column now rather than a row: the icon and copy stay side by side in
           their own header, and the preview sits under both at full width. */
        .wn-item { display: flex; flex-direction: column; padding: 14px; background-color: var(--bg-button-secondary); border: 1px solid var(--border-light); border-radius: 14px; }
        .wn-item-head { display: flex; align-items: flex-start; gap: 12px; }
        .wn-item-icon { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 10px; background-color: var(--bg-button-accent); color: var(--accent-primary); flex-shrink: 0; }
        .wn-item-text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
        .wn-item-title { font-size: 15px; font-weight: 600; color: var(--text-primary); }
        .wn-item-body { font-size: 13.5px; line-height: 1.45; color: var(--text-secondary); }

        /* ---- Feature previews ----
           The previews carry the real components' own class names, so almost
           nothing is styled here. These rules only undo the page-level padding,
           margins and column widths those classes bring with them, which a
           560px modal does not have room for. Anything beyond that belongs in
           the feature's own stylesheet, not this one. */
        .wn-preview { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-secondary); pointer-events: none; }
        .wn-preview .reps-tabs-row { padding: 0; max-width: none; }
        .wn-preview .reps-mode-row { margin-top: 0; }
        .wn-preview .feed-item { margin-bottom: 0; }
        .wn-preview .candidate-financials-coverage { margin-bottom: 8px; }
        /* The panel carries a 15px side margin for the topic page it sits on. */
        .wn-preview .upcoming-elections { margin: 0; padding: 12px; }
        .wn-preview .ue-next { padding-top: 0; }
        .wn-preview .ue-levels { padding-top: 0; }
        /* .ue-levels never wraps: on the page it holds four short tabs in a
           phone-width column and they fit. The modal is narrower -- its own
           padding, the item card's and the panel's leave about 255px on a
           phone -- and Federal, State, County and City need about 290px, so the
           row ran past the card edge. Wrapping here only, so a tab that does
           not fit drops to a second line under the first. */
        .wn-preview .ue-levels { flex-wrap: wrap; }
        /* The district preview is the only one with the controls row above the
           tabs. The tabs' top padding, zeroed above for LevelsPreview, is what
           separates the two rows on the page, so it comes back here. */
        .wn-preview .ue-controls { padding-top: 0; }
        .wn-preview .ue-controls + .ue-levels { padding-top: 10px; }
        /* The parties strip, record lines and tab strip bring page margins. */
        .wn-preview .ballot-parties { margin: 0; }
        .wn-preview .ballot-parties-row { overflow: hidden; }
        .wn-preview .record-lines { margin-top: 0; }
        .wn-preview .member-tabs { margin: 10px 0 0; overflow: hidden; }

        /* Release history. Labelled links rather than bare arrows and a
           counter, and up to two of them: with more than two releases on file
           a middle entry has somewhere to go in both directions, and wraps to
           a second line on a narrow modal rather than squeezing. */
        .wn-history { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 4px 20px 0; flex-shrink: 0; flex-wrap: wrap; }
        .wn-history-link { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: none; border-radius: 50px; background: none; color: var(--text-muted); font-size: 13px; font-family: inherit; cursor: pointer; transition: background-color 0.2s ease, color 0.2s ease; }
        .wn-history-link:hover { background-color: var(--bg-card-hover); color: var(--text-primary); }
        .wn-history-link:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }

        .wn-footer { flex-shrink: 0; padding: 12px 20px max(env(safe-area-inset-bottom, 16px), 20px); }
        .wn-done-btn { width: 100%; display: block; font-size: 17px; min-height: 46px; padding: 10px; background-color: var(--accent-primary); color: #ffffff; border: none; border-radius: 25px; cursor: pointer; box-shadow: 2px 2px 12px var(--shadow-heavy); }
        .wn-done-btn:hover { background-color: var(--accent-secondary); }
        .wn-done-btn:focus-visible { outline: 2px solid var(--text-primary); outline-offset: 2px; }
        [data-mode="dark"] .wn-done-btn { color: var(--bg-primary); }
        [data-mode="dark"] .wn-item-icon { color: var(--accent-primary); }
        [data-mode="light"] .wn-item-icon { color: var(--accent-primary); }
        @media (max-width: 480px) {
          .wn-modal { max-height: 94vh; }
          .wn-item { padding: 12px; }
          .wn-history { padding-left: 12px; padding-right: 12px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .wn-modal, .wn-item, .wn-history-link, .wn-done-btn { transition: none !important; }
        }
      `}</style>

      <div className="wn-overlay" role="dialog" aria-modal="true" aria-labelledby="wn-title">
        <div className="wn-modal">
          <div className="wn-header">
            <div className="wn-header-logo">
              <Image width="44px" src="favicon.ico" alt="" />
            </div>
            <div className="wn-header-text">
              {/* Reads the release being viewed rather than the constant, so
                  walking back to 3.5.0 says 3.5.0. */}
              <span className="wn-eyebrow">
                {t("common:whatsNewVersionLabel", "Version {{version}}", {
                  version: release.version,
                })}
              </span>
              <h5 className="wn-title" id="wn-title">
                {t("common:whatsNewTitle", "What's new")}
              </h5>
              <p className="wn-subtitle">
                {hasNewer
                  ? t("common:whatsNewOlderSubtitle", "An earlier update.")
                  : t(
                      "common:whatsNewSubtitle",
                      "Here is what changed since you were last here."
                    )}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="wn-close-btn"
              aria-label={t("common:close", "Close")}
            >
              <X size={22} />
            </button>
          </div>

          {/* key on the version so the list scrolls back to the top when the
              release changes, rather than holding the previous scroll offset. */}
          <div className="wn-content" key={release.version} aria-live="polite">
            {release.groups.map((group) => (
              <section key={group.id} className="wn-group">
                <h6 className="wn-group-label">
                  {t(`common:${group.labelKey}`, group.labelFallback)}
                </h6>
                <ul className="wn-list">
                  {group.items.map((item) => (
                    <li key={item.id} className="wn-item">
                      <div className="wn-item-head">
                        <span className="wn-item-icon">{item.icon}</span>
                        <span className="wn-item-text">
                          <span className="wn-item-title">
                            {t(`common:${item.titleKey}`, item.titleFallback)}
                          </span>
                          <span className="wn-item-body">
                            {t(`common:${item.bodyKey}`, item.bodyFallback)}
                          </span>
                        </span>
                      </div>

                      {/* Decorative: the copy above already says what changed,
                          so a screen reader gains nothing from the miniature. */}
                      {item.preview && (
                        <div aria-hidden="true">{item.preview}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          {/* Both directions are offered whenever both exist, which is what
              keeps a middle release reachable: with only the newer control
              rendered, walking back stopped at the second entry and every
              release older than it could not be opened at all.

              Nothing is rendered where there is nowhere to go, rather than a
              disabled control that invites a dead click -- so the oldest entry
              shows only the newer link, and the newest only the older one.

              The newer label depends on distance, not direction. One step from
              the front IS the latest; two or more steps back is not, and
              calling it "the latest" would promise a jump the button does not
              make. */}
          {(hasOlder || hasNewer) && (
            <div className="wn-history">
              {hasOlder && (
                <button type="button" className="wn-history-link" onClick={goOlder}>
                  <ChevronLeft size={15} />
                  {t("common:whatsNewViewPrevious", "View previous changes")}
                </button>
              )}

              {hasNewer && (
                <button type="button" className="wn-history-link" onClick={goNewer}>
                  <ChevronRight size={15} />
                  {releaseIndex === 1
                    ? t("common:whatsNewBackToLatest", "Back to the latest changes")
                    : t("common:whatsNewViewNewer", "Newer changes")}
                </button>
              )}
            </div>
          )}

          <div className="wn-footer">
            <button type="button" onClick={handleClose} className="wn-done-btn">
              {t("common:whatsNewDismiss", "Got it")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WhatsNewModal;